/**
 * 로케일 (P8-①) — 기본 한국어 사전.
 * 사용자 노출 문자열을 한곳에 모은다. `mountLumirEditor(el, { locale })`로 부분 덮어쓰기 가능.
 * (UI 모듈 내부 문자열의 전면 주입은 P9 폴리시에서 확장)
 */

export const KO = {
  placeholder: "내용을 입력하거나 '/' 를 누르세요…",
  upload: {
    uploading: '업로드 중…',
    disabled: '미디어 업로드가 비활성화되어 있습니다.',
  },
  paste: {
    imagesDropped:
      '일부 이미지는 붙여넣기로 가져올 수 없어 제외되었습니다. 이미지를 개별 복사하거나 드래그해서 넣어주세요.',
  },
  // 에러 토스트(코드별 사용자 메시지는 LumirEditorError.getUserMessage와 정합)
  errors: {
    UPLOAD_FAILED: '파일 업로드에 실패했습니다. 다시 시도해주세요.',
    INVALID_FILE_TYPE:
      '지원하지 않는 파일 형식입니다. 이미지 파일만 업로드 가능합니다.',
    S3_CONFIG_ERROR: 'S3 설정이 올바르지 않습니다. 관리자에게 문의하세요.',
    PRESIGNED_URL_ERROR: '업로드 URL 생성에 실패했습니다. 다시 시도해주세요.',
    NETWORK_ERROR: '네트워크 연결을 확인해주세요.',
    EDITOR_ERROR: '에디터 오류가 발생했습니다. 페이지를 새로고침해주세요.',
    UNKNOWN_ERROR: '알 수 없는 오류가 발생했습니다.',
  },
  toolbar: {
    bold: '굵게',
    italic: '기울임',
    underline: '밑줄',
    strike: '취소선',
    code: '코드',
    link: '링크',
    textColor: '글자색',
    bgColor: '배경색',
  },
  blockTypes: {
    paragraph: '본문',
    heading1: '제목1',
    heading2: '제목2',
    heading3: '제목3',
    heading4: '제목4',
    heading5: '제목5',
    heading6: '제목6',
    toggleHeading1: '토글 제목1',
    toggleHeading2: '토글 제목2',
    toggleHeading3: '토글 제목3',
    toggleList: '토글 목록',
    bulletListItem: '• 목록',
    numberedListItem: '1. 목록',
    checkListItem: '☑ 체크',
    quote: '인용',
    codeBlock: '코드',
  },
  slashMenu: {
    paragraph: '텍스트',
    heading1: '제목1',
    heading2: '제목2',
    heading3: '제목3',
    heading4: '제목4',
    heading5: '제목5',
    heading6: '제목6',
    toggleHeading1: '접을 수 있는 제목1',
    toggleHeading2: '접을 수 있는 제목2',
    toggleHeading3: '접을 수 있는 제목3',
    toggleList: '접을 수 있는 목록',
    bulletListItem: '글머리 기호',
    numberedListItem: '번호 매기기',
    checkListItem: '할 일',
    quote: '인용',
    codeBlock: '코드',
    emoji: '이모지',
    image: '이미지',
    video: '비디오',
    table: '표',
    pageBreak: '페이지 나누기',
    columns2: '2단 컬럼',
    columns2Divider: '2단 컬럼 (구분선)',
    groupHeading: '제목',
    groupBasic: '기본 블록',
    groupMedia: '미디어',
    groupEmbed: '임베드',
  },
  table: {
    rowAbove: '위에 행',
    rowBelow: '아래에 행',
    rowDelete: '행 삭제',
    colLeft: '왼쪽에 열',
    colRight: '오른쪽에 열',
    colDelete: '열 삭제',
    merge: '셀 병합',
    split: '병합 해제',
    alignLeft: '왼쪽',
    alignCenter: '가운데',
    alignRight: '오른쪽',
    vTop: '위',
    vMiddle: '중간',
    vBottom: '아래',
    fill: '셀 배경색',
    textColor: '셀 글자색',
    tableLeft: '표 왼쪽 정렬',
    tableCenter: '표 가운데 정렬',
    tableRight: '표 오른쪽 정렬',
    headerRow: '머리행',
    headerCol: '머리열',
    insertCheckbox: '체크박스',
    orderedList: '번호 목록',
    bulletList: '글머리 목록',
    deleteTable: '표 삭제',
  },
  link: {
    open: '열기',
    edit: '편집',
    copy: 'URL 복사',
    remove: '링크 제거',
    apply: '적용',
    cancel: '취소',
    placeholder: 'https://…',
    pageOrUrl: '페이지 또는 URL',
    linkTitle: '링크 제목',
  },
};

/** 영어 사전 (A10) — KO와 동일 구조. */
export const EN = {
  placeholder: "Type something or press '/'…",
  upload: { uploading: 'Uploading…', disabled: 'Image upload is disabled.' },
  paste: {
    imagesDropped:
      'Some images could not be pasted and were skipped. Copy or drag the images individually instead.',
  },
  errors: {
    UPLOAD_FAILED: 'File upload failed. Please try again.',
    INVALID_FILE_TYPE:
      'Unsupported file type. Only image files can be uploaded.',
    S3_CONFIG_ERROR:
      'S3 configuration is invalid. Please contact your administrator.',
    PRESIGNED_URL_ERROR: 'Failed to create upload URL. Please try again.',
    NETWORK_ERROR: 'Please check your network connection.',
    EDITOR_ERROR: 'An editor error occurred. Please refresh the page.',
    UNKNOWN_ERROR: 'An unknown error occurred.',
  },
  toolbar: {
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strike: 'Strikethrough',
    code: 'Code',
    link: 'Link',
    textColor: 'Text color',
    bgColor: 'Background color',
  },
  blockTypes: {
    paragraph: 'Text',
    heading1: 'Heading 1',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    heading4: 'Heading 4',
    heading5: 'Heading 5',
    heading6: 'Heading 6',
    toggleHeading1: 'Toggle heading 1',
    toggleHeading2: 'Toggle heading 2',
    toggleHeading3: 'Toggle heading 3',
    toggleList: 'Toggle list',
    bulletListItem: '• List',
    numberedListItem: '1. List',
    checkListItem: '☑ Check',
    quote: 'Quote',
    codeBlock: 'Code',
  },
  slashMenu: {
    paragraph: 'Text',
    heading1: 'Heading 1',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    heading4: 'Heading 4',
    heading5: 'Heading 5',
    heading6: 'Heading 6',
    toggleHeading1: 'Toggle heading 1',
    toggleHeading2: 'Toggle heading 2',
    toggleHeading3: 'Toggle heading 3',
    toggleList: 'Toggle list',
    bulletListItem: 'Bullet list',
    numberedListItem: 'Numbered list',
    checkListItem: 'Check list',
    quote: 'Quote',
    codeBlock: 'Code',
    emoji: 'Emoji',
    image: 'Image',
    video: 'Video',
    table: 'Table',
    pageBreak: 'Page break',
    columns2: '2 columns',
    columns2Divider: '2 columns (divider)',
    groupHeading: 'Headings',
    groupBasic: 'Basic blocks',
    groupMedia: 'Media',
    groupEmbed: 'Embed',
  },
  table: {
    rowAbove: 'Insert row above',
    rowBelow: 'Insert row below',
    rowDelete: 'Delete row',
    colLeft: 'Insert column left',
    colRight: 'Insert column right',
    colDelete: 'Delete column',
    merge: 'Merge cells',
    split: 'Split cells',
    alignLeft: 'Left',
    alignCenter: 'Center',
    alignRight: 'Right',
    vTop: 'Top',
    vMiddle: 'Middle',
    vBottom: 'Bottom',
    fill: 'Cell background',
    textColor: 'Cell text color',
    tableLeft: 'Align table left',
    tableCenter: 'Align table center',
    tableRight: 'Align table right',
    headerRow: 'Header row',
    headerCol: 'Header column',
    insertCheckbox: 'Checkbox',
    orderedList: 'Numbered list',
    bulletList: 'Bulleted list',
    deleteTable: 'Delete table',
  },
  link: {
    open: 'Open',
    edit: 'Edit',
    copy: 'Copy URL',
    remove: 'Remove link',
    apply: 'Apply',
    cancel: 'Cancel',
    placeholder: 'https://…',
    pageOrUrl: 'Page or URL',
    linkTitle: 'Link title',
  },
};

/** 지원 로케일: 한국어(ko) · 영어(en) 2개만. (다국어 확장 프레임워크는 채택하지 않음) */
export const LOCALES = { ko: KO, en: EN };

function mergeOver(base, over) {
  const out = { ...base };
  for (const k of Object.keys(over)) {
    const v = over[k];
    out[k] =
      v && typeof v === 'object' && !Array.isArray(v)
        ? { ...(base[k] || {}), ...v }
        : v;
  }
  return out;
}

/**
 * 로케일 해석 — 한국어/영어만 지원.
 *  - 문자열 "ko"/"en": 내장 사전 반환(그 외 코드는 KO).
 *  - 객체: KO 위에 2-레벨 머지(부분 오버라이드). `code:"en"`이면 EN을 베이스로 머지.
 *  - 없음: KO.
 */
export function resolveLocale(locale) {
  if (!locale) return KO;
  if (typeof locale === 'string') return LOCALES[locale] || KO;
  if (typeof locale !== 'object') return KO;
  const base = locale.code && LOCALES[locale.code] ? LOCALES[locale.code] : KO;
  const over = { ...locale };
  delete over.code;
  return mergeOver(base, over);
}
